import type { APIGatewayEvent, APIGatewayProxyResult, Context } from 'aws-lambda';
import { getLogger, withRequest } from '@tig-dpqa-cloud/logger';
import { ZodError } from 'zod';
import { isHttpError } from 'http-errors';

type AWSLambdaHandler = (event: APIGatewayEvent, context: Context) => Promise<APIGatewayProxyResult>;
export type HandlerResponse<T = object> = Promise<Partial<Omit<APIGatewayProxyResult, 'body'>> & { body?: string | T } | void>;
export type APIIndexFunction = (event: APIGatewayEvent, context: Context) => HandlerResponse;

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Strict-Transport-Security': 'max-age=63072000; includeSubDomains; preload',
};

const createErrorProxyResponse = (statusCode: number, message: string, reqId: string): APIGatewayProxyResult =>
  ({
    statusCode,
    body: JSON.stringify({ message }),
    isBase64Encoded: false,
    headers: {
      ...DEFAULT_HEADERS,
      'X-awsRequestId': reqId,
      'dpqa-cloud-version': process.env['DPQA_CLOUD_VERSION'] || 'unknown',
    },
  });

/**
 * Decorator for error handling and default headers of a lambda receiving an APIGatewayEvent (aka, a lambda handling API calls).
 *
 * The decorator will unwrap ZodErrors and HttpErrors and ensure no other information from other errors is leaked to the client.
 *
 * Only supports APIGatewayEvents (for now).
 *
 * @param wrappedHandler The handler function of the API call (which no longer has to handle errors itself)
 * @returns A handler function with the same signature as the handler it wraps.
 */
export const wrapHandler = (wrappedHandler: APIIndexFunction): AWSLambdaHandler =>
  async (event, ctx) => {
    const logger = getLogger();

    withRequest(event, ctx);
    try {
      const { headers, body, ...rest } = (await wrappedHandler(event, ctx)) || {};
      return {
        statusCode: body ? 200 : 204,
        body: typeof body === 'string' ? body : (JSON.stringify(body) || ''),
        headers: {
          ...DEFAULT_HEADERS,
          'X-awsRequestId': ctx.awsRequestId,
          'dpqa-cloud-version': process.env['DPQA_CLOUD_VERSION'] || 'unknown',
          ...headers,
        },
        isBase64Encoded: false,
        ...rest,
      };
    } catch (err) {
      logger.debug({ err }, 'Debugging all errors');
      // If ZodError => 400 BadRequest w/ the Zod error info
      if (err instanceof ZodError) {
      // Construct an error message based on all the info in the ZodError
        const message = err.issues
          .map(({ path, message: _message }) => ({
            message: _message,
            path: path.filter((entry) => typeof entry === 'string'),
          }))
          .map(({ path, message: _message }) => `[${path.join('.')}]: ${_message}`)
          .join('; ');
        return createErrorProxyResponse(400, message, ctx.awsRequestId);
      }

      // If http error, respond according to object info
      if (isHttpError(err)) {
        const { statusCode, message } = err;
        return createErrorProxyResponse(
          statusCode,
          err.expose ? message : `Unknown error, contact support (req: ${ctx.awsRequestId})`,
          ctx.awsRequestId,
        );
      }

      // If something else, => Internal Server Error
      logger.error(
        {
          err,
          awsRequestId: ctx.awsRequestId,
        },
        'Something went wrong',
      );
      return createErrorProxyResponse(500, `Unknown error, contact support (req: ${ctx.awsRequestId})`, ctx.awsRequestId);
    }
  };
